'use client';

import { useCallback, useEffect, useState } from 'react';

type StorageType = 'localStorage' | 'sessionStorage';

/**
 * A type-safe React hook that syncs state with browser storage (localStorage or sessionStorage)
 * @param key - The storage key
 * @param defaultValue - The default value if no stored value exists
 * @param storageType - The type of storage to use ('localStorage' or 'sessionStorage')
 * @returns A tuple with the current value and a setter function
 */
export default function useClientStorage<T>(
  key: string,
  defaultValue: T,
  storageType: StorageType = 'localStorage'
): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return defaultValue;
    }

    try {
      const storage = window[storageType];
      const item = storage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading ${storageType} key "${key}":`, error);
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;

        setStoredValue(valueToStore);

        if (typeof window !== 'undefined') {
          const storage = window[storageType];
          storage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.warn(`Error setting ${storageType} key "${key}":`, error);
      }
    },
    [key, storedValue, storageType]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleStorageChange = (e: StorageEvent) => {
      const targetStorage = window[storageType];
      if (e.key === key && e.storageArea === targetStorage) {
        try {
          const newValue = e.newValue ? JSON.parse(e.newValue) : defaultValue;
          setStoredValue(newValue);
        } catch (error) {
          console.warn(`Error parsing ${storageType} value for key "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, defaultValue, storageType]);

  return [storedValue, setValue];
}
